// frontend/src/pages/yard/YardAccounts.tsx
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '../../contexts/ToastContext';
import managedLoginService from '../../services/managedLogin.service';
import yardService from '../../services/yard.service';
import {
  ManagedLogin,
  ManagedLoginCreate,
  ManagedLoginUpdate,
} from '../../types/managedLogin';
import { YardLocation } from '../../types/yard';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import SingleSelect from '../../components/SingleSelect';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

const USER_TYPE = 'yard' as const;

export default function YardAccounts() {
  const { showToast } = useToast();

  const [logins, setLogins] = useState<ManagedLogin[]>([]);
  const [yards, setYards] = useState<YardLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLogin, setEditingLogin] = useState<ManagedLogin | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(showModal);

  // Form state — user_type pinned to 'yard' on this page.
  const [formData, setFormData] = useState<ManagedLoginCreate>({
    username: '',
    password: '',
    phone_number: '',
    user_type: USER_TYPE,
    display_name: '',
    mfa_enabled: false,
    is_active: true,
    default_location_id: undefined,
    managed_yard_ids: null,
  });
  const [confirmPassword, setConfirmPassword] = useState('');

  // Load data — only show yard-type accounts here. Other managed-login types
  // (guard, customer) live on the Administration > Managed Logins page.
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [loginsData, yardsData] = await Promise.all([
        managedLoginService.getAll(),
        yardService.getYardLocations(),
      ]);
      setLogins(loginsData.filter((l) => l.user_type === USER_TYPE));
      setYards(yardsData);
    } catch (error) {
      showToast({ message: 'Error loading data', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = () => {
    setEditingLogin(null);
    setFormData({
      username: '',
      password: '',
      phone_number: '',
      user_type: USER_TYPE,
      display_name: '',
      mfa_enabled: false,
      is_active: true,
      default_location_id: undefined,
      managed_yard_ids: null,
    });
    setConfirmPassword('');
    setShowModal(true);
  };

  const openEditModal = (login: ManagedLogin) => {
    setEditingLogin(login);
    setFormData({
      username: login.username,
      password: '',
      phone_number: login.phone_number || '',
      user_type: USER_TYPE,
      display_name: login.display_name || '',
      mfa_enabled: login.mfa_enabled,
      is_active: login.is_active,
      default_location_id: login.default_location_id || undefined,
      managed_yard_ids: login.managed_yard_ids ?? null,
    });
    setConfirmPassword('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingLogin && !formData.password) {
      showToast({ message: 'Password is required', type: 'error' });
      return;
    }

    if (!/^\+1\d{10}$/.test(formData.phone_number || '')) {
      showToast({
        message: 'Phone number must be 10 digits (US).',
        type: 'error',
      });
      return;
    }

    if (formData.password) {
      if (formData.password !== confirmPassword) {
        showToast({ message: 'Passwords do not match', type: 'error' });
        return;
      }
      const hasLetter = /[A-Za-z]/.test(formData.password);
      const hasDigit = /[0-9]/.test(formData.password);
      if (formData.password.length < 10 || !hasLetter || !hasDigit) {
        showToast({
          message: 'Password must be at least 10 characters and contain a letter and a digit.',
          type: 'error',
        });
        return;
      }
    }

    try {
      if (editingLogin) {
        const updateData: ManagedLoginUpdate = {
          display_name: formData.display_name || undefined,
          user_type: USER_TYPE,
          is_active: formData.is_active,
          default_location_id: formData.default_location_id,
          managed_yard_ids: formData.managed_yard_ids ?? null,
          phone_number: formData.phone_number,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await managedLoginService.update(editingLogin.id, updateData);
        showToast({ message: 'Account updated successfully', type: 'success' });
      } else {
        await managedLoginService.create({ ...formData, user_type: USER_TYPE });
        showToast({ message: 'Account created successfully', type: 'success' });
      }
      setShowModal(false);
      loadData();
    } catch (error: any) {
      showToast({ message: error.response?.data?.detail || 'Error saving account', type: 'error' });
    }
  };

  const handleUnlock = async (login: ManagedLogin) => {
    try {
      await managedLoginService.unlock(login.id);
      showToast({ message: 'Account unlocked.', type: 'success' });
      loadData();
    } catch (error: any) {
      showToast({
        message: error.response?.data?.detail || 'Error unlocking account',
        type: 'error',
      });
    }
  };

  const handleReactivate = async (login: ManagedLogin) => {
    try {
      await managedLoginService.reactivate(login.id);
      showToast({ message: 'Account reactivated.', type: 'success' });
      loadData();
    } catch (error: any) {
      showToast({
        message: error.response?.data?.detail || 'Error reactivating account',
        type: 'error',
      });
    }
  };

  const handleDelete = async (login: ManagedLogin) => {
    if (!confirm(`Are you sure you want to delete "${login.username}"?`)) {
      return;
    }

    try {
      await managedLoginService.delete(login.id);
      showToast({ message: 'Account deleted successfully', type: 'success' });
      loadData();
    } catch (error) {
      showToast({ message: 'Error deleting account', type: 'error' });
    }
  };

  const filteredLogins = logins.filter((login) => {
    const term = searchTerm.toLowerCase();
    return (
      login.username.toLowerCase().includes(term) ||
      (login.display_name?.toLowerCase() || '').includes(term)
    );
  });

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getYardName = (id: number | null) => {
    if (!id) return '-';
    const yard = yards.find((y) => y.recno === id);
    return yard?.locname || '-';
  };

  const yardOptions = useMemo<MultiSelectOption[]>(
    () => yards.map((y) => ({ value: String(y.recno), label: y.locname })),
    [yards],
  );

  const managedYardsLabel = (ids: number[] | null) => {
    if (!ids || ids.length === 0) return 'All yards';
    if (ids.length <= 2) {
      return ids
        .map((id) => yards.find((y) => y.recno === id)?.locname || `#${id}`)
        .join(', ');
    }
    return `${ids.length} yards`;
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold dark:text-white">Yard Account Management</h1>
        <div className="flex gap-2">
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Add Account
          </button>
        </div>
      </div>

      <p className="text-gray-600 dark:text-gray-300 mb-4">
        Manage yard staff login accounts used to sign in at the yard portal.
      </p>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by username or display name..."
          className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-panel text-body placeholder-gray-400 dark:placeholder-gray-500"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : (
        <div className="bg-panel rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Username</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Display Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Default Yard</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Managed Yards</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Last Login</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Failed Attempts</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Lockout</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Last IP</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLogins.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-muted">
                    {logins.length === 0 ? 'No yard accounts created yet' : 'No matching accounts'}
                  </td>
                </tr>
              ) : (
                filteredLogins.map((login) => {
                  const isLocked =
                    !!login.lockout_until &&
                    new Date(login.lockout_until) > new Date();
                  const showUnlock = isLocked || login.failed_login_count > 0;
                  const failedCount = login.failed_login_count ?? 0;
                  let failedBadgeClass = 'bg-gray-100 text-label dark:bg-gray-700';
                  if (failedCount >= 5) {
                    failedBadgeClass = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
                  } else if (failedCount >= 3) {
                    failedBadgeClass = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
                  }
                  return (
                    <tr key={login.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-3 font-medium">{login.username}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{login.display_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{getYardName(login.default_location_id)}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-sm">
                        {managedYardsLabel(login.managed_yard_ids)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            login.is_active
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          }`}
                        >
                          {login.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted text-sm">
                        {formatDateTime(login.last_login_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${failedBadgeClass}`}>
                          {failedCount}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isLocked ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            Locked until {formatDateTime(login.lockout_until)}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted text-xs">
                        {login.last_login_ip || '-'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditModal(login)}
                          className="text-blue-600 hover:text-blue-800 text-sm mr-3"
                        >
                          Edit
                        </button>
                        {showUnlock && login.is_active && (
                          <button
                            onClick={() => handleUnlock(login)}
                            className="text-amber-600 hover:text-amber-800 text-sm mr-3"
                          >
                            Unlock
                          </button>
                        )}
                        {!login.is_active && (
                          <button
                            onClick={() => handleReactivate(login)}
                            className="text-green-600 hover:text-green-800 text-sm mr-3"
                          >
                            Reactivate
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(login)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={panelRef}
            style={panelStyle}
            className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-md mx-4"
          >
            <ModalHeader
              title={editingLogin ? 'Edit Yard Account' : 'Create Yard Account'}
              onClose={() => setShowModal(false)}
              dragHandleProps={dragHandleProps}
            />

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  disabled={!!editingLogin}
                  className="w-full border rounded px-3 py-2 disabled:bg-gray-100 dark:disabled:bg-gray-700"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">
                  {editingLogin ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-panel text-body placeholder-gray-400 dark:placeholder-gray-500"
                  required={!editingLogin}
                />
                <p className="mt-1 text-xs text-muted">
                  Must be at least 10 characters and contain a letter and a digit.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-panel text-body placeholder-gray-400 dark:placeholder-gray-500"
                  required={!editingLogin}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Phone Number</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 rounded-l bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm">
                    +1
                  </span>
                  <input
                    type="tel"
                    value={(formData.phone_number || '').replace(/^\+1/, '')}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({
                        ...formData,
                        phone_number: digits ? `+1${digits}` : '',
                      });
                    }}
                    placeholder="5551234567"
                    maxLength={10}
                    inputMode="numeric"
                    className="w-full border rounded-r px-3 py-2"
                    required
                  />
                </div>
                <p className="text-xs text-muted mt-1">
                  10-digit US phone number. Used for mandatory MFA SMS codes.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Display Name</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-panel text-body placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Default Yard</label>
                <SingleSelect
                  options={yards.map((yard) => ({ value: String(yard.recno), label: yard.locname }))}
                  value={formData.default_location_id != null ? String(formData.default_location_id) : ''}
                  onChange={(v) =>
                    setFormData({
                      ...formData,
                      default_location_id: v ? Number(v) : undefined,
                    })
                  }
                  placeholder="None"
                  showClearOption
                  clearOptionLabel="None"
                  enableSearch
                  searchPlaceholder="Search yards…"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Managed Yards</label>
                <MultiSelect
                  options={yardOptions}
                  value={(formData.managed_yard_ids ?? []).map(String)}
                  onChange={(values) =>
                    setFormData({
                      ...formData,
                      managed_yard_ids: values.length ? values.map(Number) : null,
                    })
                  }
                  placeholder="All yards (no restriction)"
                  enableSearch
                  preserveOrder
                />
                <p className="text-xs text-muted mt-1">
                  Leave empty to allow this account to switch between any yard. When set, the
                  yard dropdown in the portal is locked to these yards.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  Active
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingLogin ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
